import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useState } from 'react';
import SearchableSelect from '@/tiktok-platform/modules/products/components/SearchableSelect';
import {
  isIllegalTiktokTryOnSizeName,
  mapModelTemplateToGoodsModelReq,
  normalizeTiktokTryOnResult,
  resolveGoodsModelTryOnSize,
  TIKTOK_TRY_ON_RESULT_OPTIONS,
  TIKTOK_TRY_ON_RESULT_TRUE_TO_SIZE,
} from '@/tiktok-platform/utils/tiktokOfficialSizeChart';
import { tiktokPlatformAPI } from '@/utils/api';

function modelRowId(row) {
  return String(row?.id || row?.modelId || row?.templateId || row?.platformModelId || '').trim();
}

function modelDisplayName(row) {
  return String(row?.name || row?.modelName || row?.payload?.modelName || '').trim()
    || (modelRowId(row) ? fixedUiTpl('模特#{0}', modelRowId(row)) : fixedUi('模特'));
}

function modelTypeLabel(row) {
  if (row?.modelTypeLabel) return fixedUi(String(row.modelTypeLabel));
  const t = row?.modelType ?? row?.payload?.modelType;
  if (t === 0 || t === '0') return fixedUi('成衣模特');
  if (t === 1 || t === '1' || t === 2 || t === '2') return fixedUi('鞋模');
  return '';
}

function modelThumb(row) {
  return String(
    row?.headPortrait
    || row?.thumbUrl
    || row?.modelProfileUrl
    || row?.payload?.headPortrait
    || row?.payload?.modelProfileUrl
    || row?.payload?.thumbUrl
    || '',
  ).trim();
}

function measureText(reqOrRow = {}) {
  const clothes = reqOrRow.clothesModel || reqOrRow.payload?.clothesModel || {};
  const shoe = reqOrRow.shoeModel || reqOrRow.payload?.shoeModel || {};
  return [
    [fixedUi('身高'), reqOrRow.modelHeight ?? reqOrRow.height ?? clothes.height],
    [fixedUi('胸围'), reqOrRow.modelBust ?? reqOrRow.bust ?? clothes.bust],
    [fixedUi('腰围'), reqOrRow.modelWaist ?? reqOrRow.waist ?? clothes.waist],
    [fixedUi('臀围'), reqOrRow.modelHip ?? reqOrRow.hip ?? clothes.hipline],
    [fixedUi('脚长'), reqOrRow.modelFootLength ?? reqOrRow.footLength ?? shoe.footLength],
    [fixedUi('脚宽'), reqOrRow.modelFootWidth ?? reqOrRow.footWidth ?? shoe.footWidth],
  ]
    .filter(([, v]) => v != null && String(v).trim() !== '')
    .map(([k, v]) => `${k} ${v}`);
}

function applyTryOnSize(req, sizeOpt) {
  if (!req || !sizeOpt) return req;
  const resolved = resolveGoodsModelTryOnSize(sizeOpt.name || sizeOpt.label, [sizeOpt]);
  if (!resolved) return req;
  return {
    ...req,
    sizeSpecId: resolved.sizeSpecId,
    sizeSpecName: resolved.sizeSpecName,
    sizeValueId: resolved.sizeValueId,
  };
}

/**
 * 发品选择模特：仅可选已同步到店铺的平台模特。
 * 试穿尺码必须来自当前商品已选规格（勿传均码）。
 */
export default function TiktokModelSelectBar({
  shopId = '',
  visible = true,
  value = [],
  onChange,
  errorMessage = '',
  /** @type {Array<{ id: string, name: string, specId?: number }>} */
  sizeOptions = [],
}) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!visible || !shopId) {
      setCatalog([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    (async () => {
      try {
        const res = await tiktokPlatformAPI.listShopModels(shopId, { page: 1, pageSize: 200 });
        if (cancelled) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        setCatalog(items.filter((row) => modelRowId(row)));
      } catch (e) {
        if (!cancelled) {
          setCatalog([]);
          setLoadError(e?.message || fixedUi('模特列表加载失败'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shopId, visible]);

  const catalogById = useMemo(() => {
    const map = new Map();
    catalog.forEach((row) => map.set(modelRowId(row), row));
    return map;
  }, [catalog]);

  const normalizedSizeOptions = useMemo(() => (
    (Array.isArray(sizeOptions) ? sizeOptions : [])
      .map((opt) => ({
        id: String(opt?.id ?? '').trim(),
        name: String(opt?.name || opt?.label || '').trim(),
        specId: Number(opt?.specId || opt?.id || 0) || 0,
      }))
      .filter((opt) => opt.id && opt.name && opt.specId > 0)
  ), [sizeOptions]);

  const sizeSelectOptions = useMemo(() => (
    normalizedSizeOptions.map((opt) => ({
      value: opt.id,
      label: opt.name,
    }))
  ), [normalizedSizeOptions]);

  const options = useMemo(() => (
    catalog.map((row) => {
      const id = modelRowId(row);
      const name = modelDisplayName(row);
      const type = modelTypeLabel(row);
      return {
        value: id,
        label: type ? `${name}（${type}）` : name,
      };
    })
  ), [catalog]);

  const selectedId = useMemo(() => {
    const first = Array.isArray(value) && value.length ? value[0] : null;
    if (!first) return '';
    const tid = String(first._templateId || first.templateId || first.modelId || '').trim();
    if (!tid) return '';
    return catalogById.has(tid) ? tid : tid;
  }, [value, catalogById]);

  const selectedRow = catalogById.get(selectedId) || null;
  const selectedReq = Array.isArray(value) && value.length ? value[0] : null;

  // 已选模特但试穿尺码非法/缺失时，按商品尺码自动纠正（仅能唯一匹配时）；试穿感受缺省合身
  const selectedSizeSpecId = Number(selectedReq?.sizeSpecId || 0);
  const selectedSizeSpecName = String(selectedReq?.sizeSpecName || '');
  const selectedSizeValueId = String(selectedReq?.sizeValueId || '');
  const selectedTryOnResult = selectedReq
    ? normalizeTiktokTryOnResult(selectedReq.tryOnResult, 0)
    : 0;
  useEffect(() => {
    if (!selectedReq) return;
    let next = null;
    const needFeel = !(selectedTryOnResult === 1 || selectedTryOnResult === 2 || selectedTryOnResult === 3);
    if (needFeel) {
      next = { ...(next || selectedReq), tryOnResult: TIKTOK_TRY_ON_RESULT_TRUE_TO_SIZE };
    }
    if (normalizedSizeOptions.length) {
      const illegal = isIllegalTiktokTryOnSizeName(selectedSizeSpecName)
        || !selectedSizeSpecName
        || !(selectedSizeSpecId > 0);
      let sizeStillValid = false;
      if (!illegal) {
        sizeStillValid = normalizedSizeOptions.some((opt) => (
          Number(opt.specId) === selectedSizeSpecId
          || String(opt.id) === selectedSizeValueId
        ));
      }
      if (!sizeStillValid) {
        const resolved = resolveGoodsModelTryOnSize(selectedSizeSpecName, normalizedSizeOptions);
        if (
          resolved
          && !(
            selectedSizeSpecId === resolved.sizeSpecId
            && selectedSizeSpecName === resolved.sizeSpecName
          )
        ) {
          next = {
            ...(next || selectedReq),
            sizeSpecId: resolved.sizeSpecId,
            sizeSpecName: resolved.sizeSpecName,
            sizeValueId: resolved.sizeValueId,
          };
        }
      }
    }
    if (next) onChange?.([next]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅随尺码/感受字段变化纠正，避免 onChange 引用抖动
  }, [
    selectedReq,
    selectedSizeSpecId,
    selectedSizeSpecName,
    selectedSizeValueId,
    selectedTryOnResult,
    normalizedSizeOptions,
  ]);

  const onSelect = (id) => {
    if (!id) {
      onChange?.([]);
      return;
    }
    const row = catalogById.get(String(id));
    if (!row) {
      onChange?.([]);
      return;
    }
    const modelId = modelRowId(row);
    let req = mapModelTemplateToGoodsModelReq(row, normalizedSizeOptions);
    if ((!req.sizeSpecId || isIllegalTiktokTryOnSizeName(req.sizeSpecName))
      && normalizedSizeOptions.length === 1) {
      req = applyTryOnSize(req, normalizedSizeOptions[0]);
    }
    onChange?.([{
      ...req,
      _templateId: modelId,
      _templateName: modelDisplayName(row),
    }]);
  };

  const onTryOnSizeChange = (sizeValueId) => {
    if (!selectedReq) return;
    if (!sizeValueId) {
      const next = { ...selectedReq };
      delete next.sizeSpecId;
      delete next.sizeSpecName;
      delete next.sizeValueId;
      onChange?.([next]);
      return;
    }
    const opt = normalizedSizeOptions.find((row) => String(row.id) === String(sizeValueId));
    if (!opt) return;
    onChange?.([applyTryOnSize(selectedReq, opt)]);
  };

  const onTryOnResultChange = (raw) => {
    if (!selectedReq) return;
    onChange?.([{
      ...selectedReq,
      tryOnResult: normalizeTiktokTryOnResult(raw),
    }]);
  };

  const tryOnFeelOptions = useMemo(() => (
    TIKTOK_TRY_ON_RESULT_OPTIONS.map((opt) => ({
      value: String(opt.value),
      label: fixedUi(opt.label),
    }))
  ), []);

  const cardMeasures = useMemo(() => {
    if (selectedReq) return measureText(selectedReq);
    if (selectedRow) return measureText(selectedRow);
    return [];
  }, [selectedReq, selectedRow]);

  const cardThumb = selectedReq?.modelProfileUrl || (selectedRow ? modelThumb(selectedRow) : '');
  const cardName = selectedReq?.modelName
    || (selectedRow ? modelDisplayName(selectedRow) : '');
  const cardType = selectedRow
    ? modelTypeLabel(selectedRow)
    : (selectedReq?.modelType === 2 ? fixedUi('鞋模') : selectedReq ? fixedUi('成衣模特') : '');

  const tryOnSizeValue = String(
    selectedReq?.sizeValueId
    || normalizedSizeOptions.find((opt) => Number(opt.specId) === Number(selectedReq?.sizeSpecId || 0))?.id
    || '',
  );
  const tryOnFeelValue = String(
    normalizeTiktokTryOnResult(selectedReq?.tryOnResult),
  );
  const tryOnFeelLabelRaw = TIKTOK_TRY_ON_RESULT_OPTIONS
    .find((opt) => opt.value === Number(tryOnFeelValue))?.label || '';
  const tryOnFeelLabel = tryOnFeelLabelRaw ? fixedUi(tryOnFeelLabelRaw) : '';

  if (!visible) return null;

  return (
    <div className="tko-model-select" data-field-error="goodsModels">
      <div className="tko-size-chart__title-row">
        <h4 className="tko-publish__subhead">{fixedUi("选择模特")}</h4>
      </div>
      {errorMessage ? <div className="tko-field__error" role="alert">{errorMessage}</div> : null}
      {loadError ? <div className="tko-field__error" role="alert">{loadError}</div> : null}

      {loading ? (
        <p className="tko-wizard__inline-hint">{fixedUi("加载模特列表…")}</p>
      ) : (
        <div className="tko-model-select__field">
          <SearchableSelect
            value={selectedId}
            onChange={onSelect}
            allowClear
            placeholder={fixedUi("从店铺模特中选择")}
            emptyText={options.length ? fixedUi('无匹配模特') : fixedUi('暂无可用模特')}
            options={options}
          />
          {!options.length ? (
            <p className="tko-wizard__inline-hint">
              {fixedUi('暂无平台模特。请先到「模板库 · 模特库」新建，或点击「拉取 TikTok 后台数据」。')}
            </p>
          ) : null}
        </div>
      )}

      {selectedReq || selectedRow ? (
        <div className="tko-model-select__card" aria-live="polite">
          <div className="tko-model-select__card-media">
            {cardThumb ? (
              <img src={cardThumb} alt="" width={72} height={72} />
            ) : (
              <span className="tko-model-select__card-placeholder" aria-hidden="true">{fixedUi("模特")}</span>
            )}
          </div>
          <div className="tko-model-select__card-body">
            <div className="tko-model-select__card-title">
              <strong>{cardName || fixedUi("已选模特")}</strong>
              {cardType ? <span className="tko-model-select__card-tag">{cardType}</span> : null}
            </div>
            {cardMeasures.length ? (
              <ul className="tko-model-select__card-metrics">
                {cardMeasures.map((text) => (
                  <li key={text}>{text}</li>
                ))}
              </ul>
            ) : (
              <p className="tko-wizard__inline-hint">{fixedUi("暂无量体信息")}</p>
            )}
            {normalizedSizeOptions.length ? (
              <div className="tko-model-select__tryon">
                <div className="tko-model-select__tryon-item">
                  <span className="tko-field__label">{fixedUi("试穿尺码")}</span>
                  <div className="tko-model-select__tryon-control">
                    <SearchableSelect
                      value={tryOnSizeValue}
                      onChange={onTryOnSizeChange}
                      allowClear={false}
                      placeholder={fixedUi("请选择商品尺码")}
                      options={sizeSelectOptions}
                    />
                  </div>
                </div>
                <div className="tko-model-select__tryon-item">
                  <span className="tko-field__label">{fixedUi("试穿感受")}</span>
                  <div className="tko-model-select__tryon-control tko-model-select__tryon-control--feel">
                    <SearchableSelect
                      value={tryOnFeelValue}
                      onChange={onTryOnResultChange}
                      allowClear={false}
                      placeholder={fixedUi("合身")}
                      options={tryOnFeelOptions}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <p className="tko-wizard__inline-hint">{fixedUi("请先在规格供应中选择尺码，再指定模特试穿尺码。")}</p>
            )}
            {(selectedReq?.sizeSpecName || selectedReq?.modelId || tryOnFeelLabel) ? (
              <p className="tko-model-select__card-meta">
                {[
                  selectedReq?.modelId ? `ID ${selectedReq.modelId}` : '',
                  selectedReq?.sizeSpecName ? fixedUiTpl('试穿 {0}', selectedReq.sizeSpecName) : '',
                  tryOnFeelLabel ? fixedUiTpl('感受 {0}', tryOnFeelLabel) : '',
                ].filter(Boolean).join(' · ')}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="tko-model-select__card-clear"
            onClick={() => onChange?.([])}
          >
            {fixedUi("清除")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
